import { useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { BsInfoCircle } from "react-icons/bs";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiRefreshCw,
  FiXCircle,
} from "react-icons/fi";
import {
  TextInputComp,
  PhoneInputComp,
  FormikDatePicker,
  FormikSelectComp,
} from "../../../../components";
import {
  useGetTitlesQuery,
  useGetMaritalStatusesQuery,
  useGetGendersQuery,
  useGetRelationshipStatusesQuery,
  useGetOccupationsQuery,
} from "../../../../store/selectableSlice";
import { useLazyCheckMemberNoAvailabilityQuery } from "../../../../store/admin/memberManagement/memberSlice";
import { useFormikContext } from "formik";
import useMemberNoAvailability, {
  MEMBER_NO_STATUS,
} from "../../../../hooks/useMemberNoAvailability";

const Section = ({ title, children, compact = false }) => (
  <div className={compact ? "mb-3" : "mb-4"}>
    <h6 className={`fw-bold text-black1 ${compact ? "mb-2 text-sm" : "mb-3 h5"}`}>
      {title}
    </h6>
    {children}
  </div>
);

const ResponsiveRow = ({ children, compact = false }) => (
  <div
    className={`row row-cols-1 ${
      compact ? "row-cols-md-2 g-3" : "row-cols-md-2 row-cols-xl-3 g-4"
    }`}
  >
    {children}
  </div>
);

const Col = ({ children, className = "" }) => (
  <div className={`col ${className}`.trim()}>{children}</div>
);

/** One line under the membership number: what we know, and what to do about it. */
export function MemberNoStatus({ status, message, onRetry }) {
  if (status === MEMBER_NO_STATUS.IDLE) return null;

  if (status === MEMBER_NO_STATUS.CURRENT) {
    return (
      <small className="d-flex align-items-center gap-2 mt-1 text-muted" role="status">
        <FiCheckCircle aria-hidden="true" size={15} />
        {message}
      </small>
    );
  }

  if (status === MEMBER_NO_STATUS.CHECKING) {
    return (
      <small className="d-flex align-items-center gap-2 mt-1 text-muted" role="status">
        <span
          className="spinner-border spinner-border-sm"
          style={{ width: 14, height: 14, borderWidth: 2 }}
          aria-hidden="true"
        />
        Checking availability…
      </small>
    );
  }

  if (status === MEMBER_NO_STATUS.AVAILABLE) {
    return (
      <small className="d-flex align-items-center gap-2 mt-1 text-success" role="status">
        <FiCheckCircle aria-hidden="true" size={15} />
        {message}
      </small>
    );
  }

  if (status === MEMBER_NO_STATUS.TAKEN) {
    return (
      <small className="d-flex align-items-center gap-2 mt-1 text-danger" role="alert">
        <FiXCircle aria-hidden="true" size={15} />
        {message}
      </small>
    );
  }

  // The check could not run. That is not the same as "taken", so say so and offer a retry.
  return (
    <div
      className="d-flex flex-wrap align-items-center gap-2 mt-2 px-2 py-2 rounded"
      style={{ background: "#fff6e5", border: "1px solid #f3d9a4", color: "#7a4b00" }}
      role="alert"
    >
      <FiAlertTriangle aria-hidden="true" size={16} style={{ flex: "none" }} />
      <small className="flex-grow-1" style={{ minWidth: 180 }}>
        {message} You can keep going. We confirm the number again when you register.
      </small>
      <button
        type="button"
        onClick={onRetry}
        className="btn btn-sm d-inline-flex align-items-center gap-1"
        style={{ border: "1px solid #7a4b00", color: "#7a4b00", background: "#fff" }}
      >
        <FiRefreshCw aria-hidden="true" size={14} />
        Try again
      </button>
    </div>
  );
}

const MEMBER_NO_TIP =
  "If you do not want the system-generated membership number, clear it and enter a custom one. It must be unique within this cooperative.";

const MEMBER_NO_EDIT_TIP =
  "You can change this number. It must be different from every other member's number in this cooperative.";

export default function PersonalDetails({
  updateImgFile,
  errorImg,
  imgFile,
  setImgFile,
  setErrorImg,
  generationError,
  isPublicSignup = false,
  branches = [],
  companyDetails = null,
  isLoadingBranches = false,
  hideOrgSummaryOnDesktop = false,
  compactLayout = false,
  onMemberNoAvailabilityChange,
  // Set on the edit screen: the member's saved number, which is never reported as taken
  originalMemberNo = "",
}) {
  const isEditing = Boolean(originalMemberNo);
  const { data: genders = [] } = useGetGendersQuery();
  const { data: titles = [] } = useGetTitlesQuery();
  const { data: maritalStatuses = [] } = useGetMaritalStatusesQuery();
  const { data: relationshipStatuses = [] } =
    useGetRelationshipStatusesQuery();
  const { data: occupations = [] } = useGetOccupationsQuery();
  const { values, setFieldValue, errors, touched, submitCount } = useFormikContext();
  // Phone fields do not render Formik errors themselves, so pass them in once the field was visited
  const phoneErrors = (name) =>
    errors[name] && (touched[name] || submitCount > 0) ? [errors[name]] : [];

  const [checkMemberNo] = useLazyCheckMemberNoAvailabilityQuery();
  const memberNo = useMemberNoAvailability(values.member_no, {
    check: (value) => checkMemberNo(value).unwrap(),
    enabled: !isPublicSignup,
    currentMemberNo: originalMemberNo,
  });

  // When the admin has typed a new, free number, say what it replaces
  const memberNoMessage =
    isEditing && memberNo.status === MEMBER_NO_STATUS.AVAILABLE
      ? `Available. This will replace ${originalMemberNo}.`
      : memberNo.message;

  // Check if "Others" is selected (occupation_id will be "others")
  const isOtherOccupation = values.occupation_id === "others";

  // Tell the form what we know, so it can decide whether to let the admin continue.
  useEffect(() => {
    if (isPublicSignup || typeof onMemberNoAvailabilityChange !== "function") {
      return;
    }
    onMemberNoAvailabilityChange({
      status: memberNo.status,
      isChecking: memberNo.isChecking,
      isAvailable: memberNo.isAvailable,
      message: memberNo.message,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberNo.status, memberNo.message, isPublicSignup]);

  return (
    <div>
      {isPublicSignup && (
        <Section title="Your branch" compact={compactLayout}>
          {hideOrgSummaryOnDesktop && (
            <div className="d-lg-none rounded border border-[#e0e0e0] bg-[#fafafa] px-3 py-2 mb-2">
              <p className="mb-0 text-sm fw-semibold text-[#383838]">
                {companyDetails?.name || "Cooperative society"}
              </p>
              {companyDetails?.address ? (
                <p className="mb-0 text-xs text-[#525252]">{companyDetails.address}</p>
              ) : null}
            </div>
          )}
          {!hideOrgSummaryOnDesktop && (
            <div className="rounded border border-[#e0e0e0] bg-[#fafafa] px-3 py-2 mb-2">
              <p className="mb-0 text-sm fw-semibold text-[#383838]">
                {companyDetails?.name || "Cooperative society"}
              </p>
              {companyDetails?.address ? (
                <p className="mb-0 text-xs text-[#525252]">{companyDetails.address}</p>
              ) : null}
            </div>
          )}
          <ResponsiveRow compact={compactLayout}>
            <Col className="col-12">
              <FormikSelectComp
                labelClassName={"fw-bold"}
                fieldName={"branch_id"}
                labelText={"Branch *"}
                selectText={
                  isLoadingBranches ? "Loading branches…" : "Select your branch"
                }
                options={
                  Array.isArray(branches) && branches.length > 0
                    ? branches.map((branch) => ({
                        value: branch.id,
                        label: branch.name,
                      }))
                    : []
                }
                disabled={isLoadingBranches}
              />
              {!isLoadingBranches && Array.isArray(branches) && branches.length > 0 && (
                <small className="text-muted d-none d-lg-block mt-1">
                  Branch list is also shown in the panel on the right.
                </small>
              )}
            </Col>
          </ResponsiveRow>
        </Section>
      )}

      <Section title="Member Information" compact={compactLayout}>
        <ResponsiveRow compact={compactLayout}>
          <Col>
            <FormikSelectComp
              labelClassName={"fw-bold"}
              fieldName={"title_id"}
              labelText={"Title"}
              selectText="Select title"
              options={titles.map((title) => ({
                value: title.id,
                label: title.name,
              }))}
            />
          </Col>
          {!isPublicSignup && (
            <Col>
              <div className="d-flex align-items-center gap-2 mb-1">
                <label className="fw-bold mb-0">Membership Number</label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="member-no-info-tip">
                      {isEditing ? MEMBER_NO_EDIT_TIP : MEMBER_NO_TIP}
                    </Tooltip>
                  }
                >
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Membership number help"
                    className="d-inline-flex align-items-center"
                    style={{ cursor: "help", color: "#31778c" }}
                  >
                    <BsInfoCircle size={16} />
                  </span>
                </OverlayTrigger>
              </div>
              <TextInputComp
                placeholder={
                  generationError
                    ? "Enter a custom membership number"
                    : "Auto generated — editable"
                }
                name="member_no"
              />
              <MemberNoStatus
                status={memberNo.status}
                message={memberNoMessage}
                onRetry={memberNo.retry}
              />
            </Col>
          )}
          <Col>
            <TextInputComp
              LabelText={"First name"}
              LabelClassName={"fw-bold"}
              placeholder={"First name"}
              name="first_name"
            />
          </Col>
          <Col>
            <TextInputComp
              LabelText={"Middle name"}
              LabelClassName={"fw-bold"}
              placeholder={"Middle name"}
              name="other_name"
            />
          </Col>
          <Col>
            <TextInputComp
              LabelText={"Last name"}
              LabelClassName={"fw-bold"}
              placeholder={"Last name"}
              name="last_name"
            />
          </Col>
          <Col>
            <FormikSelectComp
              labelClassName={"fw-bold"}
              fieldName={"occupation_id"}
              labelText={"Occupation"}
              selectText="Select occupation"
              options={[
                ...occupations.map((occupation) => ({
                  value: occupation.id,
                  label: occupation.name,
                })),
                {
                  value: "others",
                  label: "Others",
                },
              ]}
              onValueChange={(value) => {
                if (value === "others") {
                  // Clear occupation when "Others" is selected
                  setFieldValue("occupation", "");
                } else {
                  // Set occupation name when a predefined option is selected
                  const selectedOccupation = occupations.find(
                    (occ) => occ.id === parseInt(value)
                  );
                  if (selectedOccupation) {
                    setFieldValue("occupation", selectedOccupation.name);
                  }
                }
              }}
            />
            {isOtherOccupation && (
              <div className="mt-2">
                <TextInputComp
                  LabelText={"Specify Occupation"}
                  LabelClassName={"fw-bold"}
                  placeholder={"Enter your occupation"}
                  name="occupation"
                />
              </div>
            )}
          </Col>
        </ResponsiveRow>
      </Section>

      <Section title="Contact Details" compact={compactLayout}>
        <ResponsiveRow compact={compactLayout}>
          <Col>
            <TextInputComp
              LabelText={"Email (Optional)"}
              LabelClassName={"fw-bold"}
              placeholder={"email@gmail.com"}
              name="email"
            />
            <small className="text-muted d-block mt-1">
              Email is optional. Phone number is required.
            </small>
          </Col>
          <Col>
            <PhoneInputComp
              labelText={"Phone Number"}
              LabelClassName={"fw-bold"}
              placeholder={"Enter phone number"}
              name="phone"
              value={values.phone}
              onChange={(phone) => setFieldValue('phone', phone)}
              errors={phoneErrors("phone")}
            />
            <small className="text-muted d-block mt-1">
              Select country code and enter your phone number
            </small>
          </Col>
          <Col className="col-12">
            <TextInputComp
              LabelText={"Home Address"}
              LabelClassName={"fw-bold"}
              placeholder={"Address"}
              name="address"
            />
          </Col>
          <Col>
            <FormikSelectComp
              labelClassName={"fw-bold"}
              fieldName={"gender_id"}
              selectText="Select gender"
              labelText={"Gender"}
              options={genders.map((gender) => ({
                value: gender.id,
                label: gender.name,
              }))}
            />
          </Col>
          <Col>
            <FormikDatePicker
              fieldName={"dob"}
              labelText={"Date of birth"}
              labelClassName={"fw-bold"}
            />
          </Col>
          <Col>
            <FormikSelectComp
              labelClassName={"fw-bold"}
              fieldName={"marital_id"}
              labelText={"Marital Status"}
              selectText="Select marital status"
              options={maritalStatuses.map((marital) => ({
                value: marital.id,
                label: marital.name,
              }))}
            />
          </Col>
        </ResponsiveRow>
      </Section>

      <Section title="Next of Kin" compact={compactLayout}>
        <ResponsiveRow compact={compactLayout}>
          <Col>
            <TextInputComp
              LabelText={"Name"}
              LabelClassName={"fw-bold"}
              placeholder={"Full name"}
              name="next_of_kin_name"
            />
          </Col>
          <Col>
            <PhoneInputComp
              labelText={"Phone Number"}
              LabelClassName={"fw-bold"}
              placeholder={"Enter phone number"}
              name="next_of_kin_phone"
              value={values.next_of_kin_phone}
              onChange={(phone) => setFieldValue('next_of_kin_phone', phone)}
              errors={phoneErrors("next_of_kin_phone")}
            />
          </Col>
          <Col>
            <FormikSelectComp
              labelClassName={"fw-bold"}
              fieldName={"relationship_status_id"}
              labelText={"Relationship"}
              selectText="Select relationship"
              options={relationshipStatuses.map((relationship) => ({
                value: relationship.id,
                label: relationship.name,
              }))}
            />
          </Col>
        </ResponsiveRow>
      </Section>

      <Section title="Profile photo (optional)" compact={compactLayout}>
        <div className="d-flex flex-column flex-md-row align-items-start gap-3">
          <div>
            <label className="fw-2" htmlFor="imgFile" style={{ display: "block", cursor: "pointer" }}>
              <span className="mb-2 d-block text-black1">Upload photo</span>
              <div style={{ position: "relative", display: "inline-block" }}>
                {imgFile ? (
                  <img
                    src={URL.createObjectURL(imgFile)}
                    className="d-block rounded"
                    width={"88"}
                    height={"88"}
                    alt="profile preview"
                    style={{
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "2px solid #e0e0e0",
                    }}
                  />
                ) : (
                  <img
                    src="/FileUploader/upload.svg"
                    className="d-block"
                    width={"88"}
                    height={"88"}
                    alt="upload"
                  />
                )}
                {imgFile && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-8px",
                      backgroundColor: "#ff4343",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      border: "2px solid white",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (setImgFile) {
                        setImgFile(null);
                      }
                      if (setErrorImg) {
                        setErrorImg("Click to upload image");
                      }
                      // Reset the file input
                      const fileInput = document.getElementById("imgFile");
                      if (fileInput) {
                        fileInput.value = "";
                      }
                    }}
                    title="Remove image"
                  >
                    <span style={{ color: "white", fontSize: "14px", lineHeight: "1" }}>×</span>
                  </div>
                )}
              </div>
              <input
                onChange={updateImgFile}
                name="file"
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                style={{ display: "none" }}
                id="imgFile"
              />
            </label>
          </div>
          <div className="d-flex flex-column justify-content-center">
            {imgFile && (
              <>
                <small className="d-block mb-2 fw-semibold text-black1">{imgFile?.name}</small>
                <small className="text-muted d-block">
                  {(imgFile?.size / 1024 / 1024).toFixed(2)} MB
                </small>
              </>
            )}
            {errorImg && !errorImg.includes("Click to upload") && (
              <small className="text-danger mt-2 d-block">{errorImg}</small>
            )}
            {!imgFile && !errorImg && (
              <small className="text-muted d-block">
                JPG or PNG. Max size 10MB. Optional.
              </small>
            )}
            {!imgFile && errorImg === "Click to upload image" && (
              <small className="text-muted d-block">{errorImg}</small>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
